/*
 * Copyright (C) 2025 SimpliCity Digital Inc - All Rights Reserved
 */

import React, { useState, useRef, useEffect } from "react";
import { TestimonialComponent } from "./testimonialComponent";

const TestimonialCarousel = (props) => {
    const { testimonialList } = props;
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isMouseDown, setIsMouseDown] = useState(false);
    const [startX, setStartX] = useState(0);
    const [dragDistance, setDragDistance] = useState(0);

    const carouselRef = useRef(null);
    const carouselItemsRef = useRef(null);

    const totalSlides = testimonialList.length;
    const itemsPerSlide = window.innerWidth <= 834 ? 1 : 2; // 1 on mobile, 2 on desktop

    // Calculate number of visible slides (bullets)
    const visibleSlides = itemsPerSlide === 1 ? totalSlides : Math.ceil(totalSlides / 2);

    // Update carousel position based on currentIndex
    const updateCarouselPosition = () => {
        const transformValue = -(currentIndex * (100 / itemsPerSlide)) + "%";
        carouselItemsRef.current.style.transform = `translateX(${transformValue})`;
    };

    // Handle mouse/touch start
    const startDrag = (e) => {
        setIsMouseDown(true);
        setStartX(e.pageX || e.touches[0].pageX);
        setDragDistance(0);
        e.preventDefault();
        carouselRef.current.style.cursor = "grabbing";
    };

    // Handle mouse/touch movement
    const duringDrag = (e) => {
        if (!isMouseDown) {
            return;
        }

        const currentX = e.pageX || e.touches[0].pageX;
        const moveDistance = currentX - startX;

        setDragDistance(moveDistance);

        carouselItemsRef.current.style.transform = `translateX(${
            -(currentIndex * (100 / itemsPerSlide)) + moveDistance / 5
        }px)`;
    };

    const stopDrag = () => {
        if (!isMouseDown) {
            return;
        }

        if (dragDistance < -100) {
            setCurrentIndex(Math.min(currentIndex + 1, visibleSlides - 1)); // Move to next slide
        } else if (dragDistance > 100) {
            setCurrentIndex(Math.max(currentIndex - 1, 0)); // Move to previous slide
        }

        setIsMouseDown(false);
        carouselRef.current.style.cursor = "grab";
        updateCarouselPosition();
    };

    useEffect(() => {
        updateCarouselPosition();
    }, [currentIndex, updateCarouselPosition]);

    return (
        <div className="carousel" ref={carouselRef}>
            <div
                className="carousel-item"
                ref={carouselItemsRef}
                onMouseDown={startDrag}
                onMouseMove={duringDrag}
                onMouseUp={stopDrag}
                onMouseLeave={stopDrag}
                onTouchStart={startDrag}
                onTouchMove={duringDrag}
                onTouchEnd={stopDrag}
            >
                {testimonialList.map((item, index) => (
                    <TestimonialComponent item={item} key={index} />
                ))}
            </div>
            <div className="carousel-dots">
                {Array.from({ length: visibleSlides }).map((_, index) => (
                    <span
                        key={index}
                        className={`dot ${index === currentIndex ? "active" : ""}`}
                        onClick={() => setCurrentIndex(index)}
                    ></span>
                ))}
            </div>
        </div>
    );
};

TestimonialCarousel.displayName = "TestimonialCarousel";
export { TestimonialCarousel };
